import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useTranslate from '../../../core/languages/hooks/useTranslate';
import { SupplierVehiclePrice } from '../../types/TransferRoute';
import { getSupplierVehicleChoices } from '../../enums/TransferVehicleEnum';
import { TransferDirectionEnum } from '../../enums/TransferDirectionEnum';

interface VehiclePricesSelectorProps {
    vehicles: SupplierVehiclePrice[];
    fieldName: string;
    label?: string;
    direction?: TransferDirectionEnum;
}

/**
 * Vehicle selector component with increased price
 * Displays available vehicles with the increased lovePrice
 * The increase of 15 is only applied in DEPARTURE
 */
const VehiclePricesSelector: React.FC<VehiclePricesSelectorProps> = ({
    vehicles,
    fieldName,
    label,
    direction,
}) => {
    const t = useTranslate();
    const { control, formState: { errors } } = useFormContext();
    const vehicleChoices = getSupplierVehicleChoices(t);

    // Calculate price by adding 15 only if it's DEPARTURE
    const calculateIncreasedPrice = (vehicle: SupplierVehiclePrice): number => {
        const lovePrice = Number(vehicle.lovePrice);
        const additionalPrice = direction === TransferDirectionEnum.DEPARTURE ?
            Number(vehicle.departureIndividualAdditionalPrice) : Number(vehicle.arrivalIndividualAdditionalPrice);
        return lovePrice + additionalPrice;
    };

    // Get the translated label directly from the enum
    const getVehicleLabel = (vehicleType: string | number): string => {
        const enumValue = Number(vehicleType);
        const choice = vehicleChoices.find(c => c.id === enumValue);
        return choice?.label || String(vehicleType);
    };
    if (!vehicles || vehicles.length === 0) {
        return (
            <div className="text-center py-4 text-neutral-500">
                {t('transfers.message.noVehiclesAvailable')}
            </div>
        );
    }
    return (
        <Controller
            name={fieldName}
            control={control}
            rules={{ required: t('common.feedback.field_required') }}
            render={({ field }) => (
                <div>
                    {label && (
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                            {label}
                        </label>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vehicles.map((vehicle) => {
                            // Same as <select>: store string so "0" (SEDAN) is truthy like transport airplane
                            const vehicleId = String(vehicle.supplierVehicle);
                            const increasedPrice = calculateIncreasedPrice(vehicle);
                            const isSelected = String(field.value ?? "") === vehicleId;

                            return (
                                <button
                                    key={vehicleId}
                                    type="button"
                                    onClick={() => field.onChange(vehicleId)}
                                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                            : 'border-neutral-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-600'
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className="font-semibold text-neutral-900 dark:text-white">
                                                {getVehicleLabel(vehicle.supplierVehicle)}
                                            </h4>
                                            <div className="flex gap-4 mt-2">
                                                <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                                                    <i className="las la-users text-neutral-500 dark:text-neutral-400 text-2xl"></i>
                                                    <span>{vehicle.maxNumberPassengers}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                                                    <i className="las la-suitcase-rolling text-neutral-500 dark:text-neutral-400 text-2xl"></i>
                                                    <span>{vehicle.luggageCapacity}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-green-600 dark:text-green-400">
                                                {vehicle.currencySymbol}{increasedPrice.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                    {isSelected && (
                                        <div className="mt-2 flex items-center gap-2">
                                            <input
                                                type="radio"
                                                checked={true}
                                                onChange={() => {}}
                                                className="w-4 h-4 text-blue-600"
                                            />
                                            <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                                {t('common.actions.selected')}
                                            </span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {errors[fieldName] && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                            {(errors[fieldName] as any)?.message}
                        </p>
                    )}
                </div>
            )}
        />
    );
};
export default VehiclePricesSelector;
